import React from 'react';
import { Warehouse, Store, MapPin, Check, Package } from 'lucide-react';
import { LocationInfo } from '../../types';

interface LocationSelectorProps {
  locations: LocationInfo[];
  selectedId: string | null;
  onSelect: (locationId: string) => void;
  mode?: 'source' | 'destination';
  productQuantities?: Record<string, number>;
  disabled?: boolean;
  label?: string;
  compact?: boolean;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  locations,
  selectedId,
  onSelect,
  mode = 'source',
  productQuantities,
  disabled = false,
  label,
  compact = false
}) => {
  const getLocationIcon = (name: string) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('warehouse') || lowerName.includes('wearhouse')) {
      return Warehouse;
    }
    if (lowerName.includes('shop') || lowerName.includes('store')) {
      return Store;
    }
    return MapPin;
  };

  const getLocationColor = (name: string, isSelected: boolean) => {
    const lowerName = name.toLowerCase();
    if (isSelected) {
      if (mode === 'source') {
        return 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 ring-2 ring-teal-500/20';
      }
      return 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500/20';
    }
    return 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800';
  };

  const getIconColor = (name: string, isSelected: boolean) => {
    if (isSelected) {
      return mode === 'source' ? 'text-teal-600 dark:text-teal-400' : 'text-blue-600 dark:text-blue-400';
    }
    const lowerName = name.toLowerCase();
    if (lowerName.includes('warehouse') || lowerName.includes('wearhouse')) {
      return 'text-amber-600 dark:text-amber-400';
    }
    if (lowerName.includes('shop') || lowerName.includes('store')) {
      return 'text-green-600 dark:text-green-400';
    }
    return 'text-gray-500 dark:text-gray-400';
  };

  if (compact) {
    return (
      <div className="space-y-1.5">
        {label && (
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
            {label}
          </label>
        )}
        <div className="flex flex-wrap gap-2">
          {locations.map(location => {
            const isSelected = location.id === selectedId;
            const Icon = getLocationIcon(location.name);
            const stockQty = productQuantities?.[location.id];

            return (
              <button
                key={location.id}
                type="button"
                onClick={() => !disabled && onSelect(location.id)}
                disabled={disabled}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all ${
                  getLocationColor(location.name, isSelected)
                } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Icon className={`h-3.5 w-3.5 ${getIconColor(location.name, isSelected)}`} />
                <span className={`text-sm font-medium ${
                  isSelected
                    ? mode === 'source' ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {location.name}
                </span>
                {stockQty !== undefined && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isSelected
                      ? mode === 'source' ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300' : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {stockQty}
                  </span>
                )}
                {isSelected && (
                  <Check className={`h-3.5 w-3.5 ${
                    mode === 'source' ? 'text-teal-600 dark:text-teal-400' : 'text-blue-600 dark:text-blue-400'
                  }`} />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <div className="grid grid-cols-2 gap-2">
        {locations.map(location => {
          const isSelected = location.id === selectedId;
          const Icon = getLocationIcon(location.name);
          const stockQty = productQuantities?.[location.id];

          return (
            <button
              key={location.id}
              type="button"
              onClick={() => !disabled && onSelect(location.id)}
              disabled={disabled}
              className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                getLocationColor(location.name, isSelected)
              } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {isSelected && (
                <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center ${
                  mode === 'source' ? 'bg-teal-500' : 'bg-blue-500'
                }`}>
                  <Check className="h-3 w-3 text-white" />
                </div>
              )}

              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isSelected
                  ? mode === 'source' ? 'bg-teal-100 dark:bg-teal-900/50' : 'bg-blue-100 dark:bg-blue-900/50'
                  : 'bg-gray-100 dark:bg-gray-800'
              }`}>
                <Icon className={`h-6 w-6 ${getIconColor(location.name, isSelected)}`} />
              </div>

              <div className="text-center">
                <p className={`font-semibold ${
                  isSelected
                    ? mode === 'source' ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300'
                    : 'text-gray-900 dark:text-gray-100'
                }`}>
                  {location.name}
                </p>
                {mode === 'source' && stockQty !== undefined && (
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <Package className="h-3 w-3 text-gray-400" />
                    <span className={`text-xs ${
                      isSelected
                        ? 'text-teal-600 dark:text-teal-400'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {stockQty} available
                    </span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
